import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";
import type { PrintFailureReason } from "@luckymenu/core";
import type { PrinterStatus } from "@luckymenu/print-core";
import type { PrintTransportErrorCode } from "../adapters";

/**
 * Transport errors and the words staff see for them (printing.md, Failure:
 * "the reason in plain words … and a Retry button"; P5-T23).
 */

/** What a web transport throws. The Android module rejects with the same codes. */
export class PrintTransportError extends Error {
	readonly code: PrintTransportErrorCode;
	constructor(code: PrintTransportErrorCode, message?: string) {
		super(message ?? code);
		this.name = "PrintTransportError";
		this.code = code;
	}
}

const CODES = new Set<string>([
	"unreachable",
	"paper_out",
	"cover_open",
	"offline",
	"partial_write",
	"timeout",
	"permission",
	"not_supported",
	"refused",
]);

/**
 * The Android module's own codes (`PrinterError`, lucky-printer): all but
 * `ERR_PARTIAL_WRITE` mean nothing was sent.
 */
const ALIASES: Record<string, PrintTransportErrorCode> = {
	not_on_lan: "unreachable",
	bluetooth_off: "unreachable",
	bad_address: "unreachable",
	no_module: "unreachable",
	no_status: "refused",
	printer: "refused",
};

/** The transport code of any error: `ERR_UNREACHABLE`, `unreachable` and `Unreachable` alike. */
export function transportCodeOf(error: unknown): PrintTransportErrorCode | null {
	const raw = (error as { code?: unknown } | null)?.code;
	if (typeof raw !== "string") return null;
	const code = raw.toLowerCase().replace(/^err_/, "");
	if (CODES.has(code)) return code as PrintTransportErrorCode;
	return ALIASES[code] ?? null;
}

/**
 * Why a search for printers failed (the add printer wizard, P5-T24): the
 * i18n key under `printing:wizard.discoverProblems`. The Android module's
 * own codes tell a refused permission, Bluetooth switched off and a phone
 * not on Wi-Fi apart.
 */
export type DiscoverProblem = "permission" | "bluetoothOff" | "notOnLan" | "failed";

export function discoverProblemOf(error: unknown): DiscoverProblem {
	const raw = (error as { code?: unknown } | null)?.code;
	const code = typeof raw === "string" ? raw.toLowerCase().replace(/^err_/, "") : "";
	if (code === "permission") return "permission";
	if (code === "bluetooth_off") return "bluetoothOff";
	if (code === "not_on_lan") return "notOnLan";
	return "failed";
}

/** A short detail for the job's `last_error_detail`: the transport's own message. */
export function errorDetail(error: unknown): string | undefined {
	const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
	return message ? message.slice(0, 480) : undefined;
}

/**
 * Why a job failed, from what the transport threw while sending. Only a
 * failure the transport says happened before any byte went out is
 * `unreachable` (or a printer state); anything else may have printed part
 * of the job, so it is a partial write: never sent again by itself.
 */
export function sendFailureReason(
	error: unknown,
): Exclude<PrintFailureReason, "may_have_printed" | "no_rasteriser"> {
	switch (transportCodeOf(error)) {
		case "unreachable":
		case "permission":
			return "unreachable";
		case "paper_out":
			return "paper_out";
		case "cover_open":
			return "cover_open";
		case "offline":
			return "offline";
		case "not_supported":
		case "refused":
			return "other";
		default:
			// `partial_write`, `timeout`, or an error without a known code.
			return "partial_write";
	}
}

/**
 * Whether a status answer blocks printing: only a definite paper out, cover
 * open or offline does (printing.md, Known printer behaviour). Near end,
 * cutter and recoverable errors print anyway.
 */
export function blockingState(
	status: PrinterStatus,
): "paper_out" | "cover_open" | "offline" | null {
	if (status.paperOut) return "paper_out";
	if (status.coverOpen) return "cover_open";
	if (!status.online) return "offline";
	return null;
}

/** The API could not be reached: the report waits on the device and goes later (P5-T22). */
export function isOffline(error: unknown): boolean {
	return errorCodeOf(error) === "NETWORK";
}

/** The device no longer holds the job (someone else took it, or it was handled). */
export function isNotHolder(error: unknown): boolean {
	return errorCodeOf(error) === "CONFLICT" && errorDetailsOf(error).reason === "not_holder";
}

/**
 * Plain words for a failure reason: the i18n key under `printing:reasons`.
 * Kept as data so both apps show the same words.
 */
export const FAILURE_REASON_KEYS: Record<PrintFailureReason, string> = {
	paper_out: "reasons.paperOut",
	cover_open: "reasons.coverOpen",
	offline: "reasons.offline",
	unreachable: "reasons.unreachable",
	partial_write: "reasons.partialWrite",
	may_have_printed: "reasons.mayHavePrinted",
	no_rasteriser: "reasons.noRasteriser",
	other: "reasons.other",
};

/** Whether printing the job again could give a second copy: the person must check the paper first. */
export function mightHavePrinted(reason: PrintFailureReason | null): boolean {
	return reason === "partial_write" || reason === "may_have_printed";
}

export type PrintProblem =
	| "notAllowed"
	| "noPrinter"
	| "printerOff"
	| "billVoid"
	| "notInPlan"
	| "offline"
	| "notFound"
	| "failed";

/** A refused printing call, for a message under the button that made it. */
export function printProblemOf(error: unknown): PrintProblem {
	const code = errorCodeOf(error);
	const reason = errorDetailsOf(error).reason;
	if (code === "NETWORK") return "offline";
	if (code === "FORBIDDEN") return reason === "not_in_plan" ? "notInPlan" : "notAllowed";
	if (code === "NOT_FOUND") return "notFound";
	if (reason === "no_printer") return "noPrinter";
	if (reason === "printer_inactive") return "printerOff";
	if (reason === "bill_void") return "billVoid";
	return "failed";
}
